'use client';

import { RAIL_MENU_ROW, RAIL_MENU_ACTIVE, RAIL_MENU_LABEL } from './rail-menu-styles';

import { Box, Button, HStack, Input, Text, VStack } from '@chakra-ui/react';
import { History, LayoutTemplate, Plus, Search } from 'lucide-react';
import NextLink from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export const AGENTS_RAIL_SLOT_ID = 'agents-rail-panel';

type AgentRailItem = {
  id: string;
  name: string;
  detail: string;
  status: string;
  onSelect: () => void;
};
type AgentRailSuggestion = {
  id: string;
  name: string;
  description: string;
  onSelect: () => void;
};

function useAgentRailSlot(slotId: string) {
  const [target, setTarget] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const existing = document.getElementById(slotId);
    if (existing) {
      setTarget(existing);
      return;
    }
    // The shell can mount after the lazy workspace; stop observing once its slot exists.
    const observer = new MutationObserver(() => {
      const slot = document.getElementById(slotId);
      if (slot) {
        setTarget(slot);
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [slotId]);
  return target;
}

/** Page filters stay with their content; rail navigation has a single owner. */
export function AgentsRailControls({
  navigation,
  filters,
  actions,
}: {
  navigation: ReactNode;
  filters: ReactNode;
  actions: ReactNode;
}) {
  return (
    <>
      <Box display={{ base: 'block', lg: 'none' }} px="3" py="2">
        {navigation}
      </Box>
      {filters || actions ? (
        <HStack gap="3" px="4" py="2" justify="space-between" flexWrap="wrap">
          {filters}
          {actions}
        </HStack>
      ) : null}
    </>
  );
}

/** Uses the workspace's loaded data and actions, without a second fetch or editor. */
export function AgentsRailPanel({
  items,
  suggestions,
  loading,
  selectedId,
  onCreate,
}: {
  items: AgentRailItem[];
  suggestions: AgentRailSuggestion[];
  loading: boolean;
  selectedId?: string;
  onCreate: () => void;
}) {
  const target = useAgentRailSlot(AGENTS_RAIL_SLOT_ID);
  const pathname = usePathname();
  const [query, setQuery] = useState('');
  if (!target) return null;
  const search = query.trim().toLowerCase();
  const matches = items.filter(item =>
    `${item.name} ${item.detail}`.toLowerCase().includes(search)
  );
  const sections = [
    { label: 'Scheduled', items: matches.filter(item => item.status === 'enabled') },
    { label: 'Paused', items: matches.filter(item => item.status === 'paused') },
    {
      label: 'Other agents',
      items: matches.filter(item => !['enabled', 'paused'].includes(item.status)),
    },
  ];
  return createPortal(
    <VStack align="stretch" gap="5" px="0" pb="5" aria-label="Agents">
      <VStack align="stretch" gap="0.5">
        <Button
          variant="ghost"
          onClick={onCreate}
          {...RAIL_MENU_ROW}
        >
          <Plus size={17} /> New agent
        </Button>
        <Button
          asChild
          variant="ghost"
          {...RAIL_MENU_ROW}
          {...(pathname === '/app/agents/templates' ? RAIL_MENU_ACTIVE : {})}
        >
          <NextLink
            href="/app/agents/templates"
            aria-current={pathname === '/app/agents/templates' ? 'page' : undefined}
          >
            <LayoutTemplate size={17} /> Templates
          </NextLink>
        </Button>
        <Button
          asChild
          variant="ghost"
          {...RAIL_MENU_ROW}
          {...(pathname === '/app/agents/runs' ? RAIL_MENU_ACTIVE : {})}
        >
          <NextLink
            href="/app/agents/runs"
            aria-current={pathname === '/app/agents/runs' ? 'page' : undefined}
          >
            <History size={17} /> Run history
          </NextLink>
        </Button>
        <HStack px="3" pt="3" gap="2" color="ui.text.tertiary">
          <Search size={15} aria-hidden="true" />
          <Input
            aria-label="Search agents"
            placeholder="Search agents"
            value={query}
            onChange={event => setQuery(event.target.value)}
            variant="flushed"
            size="sm"
            minW={0}
          />
        </HStack>
      </VStack>
      {loading ? (
        <Text px="3" fontSize="sm" color="ui.text.tertiary" role="status">
          Loading agents…
        </Text>
      ) : null}
      {!loading && !matches.length ? (
        <Text px="3" fontSize="sm" color="ui.text.secondary" role="status">
          {search ? 'No matching agents.' : 'No agents yet. Create one or choose a template.'}
        </Text>
      ) : null}
      {sections
        .filter(section => section.items.length)
        .map(section => (
          <VStack key={section.label} align="stretch" gap="1">
            <HStack justify="space-between" px="3" pb="1">
              <Text {...RAIL_MENU_LABEL}>{section.label}</Text>
              <Text fontSize="xs" color="ui.text.tertiary">
                {section.items.length}
              </Text>
            </HStack>
            {section.items.map(item => (
              <Button
                key={item.id}
                variant="ghost"
                h="auto"
                display="block"
                textAlign="left"
                whiteSpace="normal"
                px="3"
                py="2.5"
                borderRadius="md"
                onClick={item.onSelect}
                bg={selectedId === item.id ? 'ui.surface.selected' : 'transparent'}
                _hover={{ bg: 'ui.surface.rowHover' }}
                _focusVisible={{ outline: '2px solid', outlineColor: 'ui.border.accent' }}
              >
                <Text
                  fontSize="14px"
                  lineHeight="20px"
                  fontWeight="500"
                  color="ui.text.primary"
                  lineClamp={2}
                >
                  {item.name}
                </Text>
                <Text mt="1" fontSize="xs" fontWeight="400" color="ui.text.tertiary" lineClamp={2}>
                  {item.detail}
                </Text>
              </Button>
            ))}
          </VStack>
        ))}
      {!search && suggestions.length > 0 ? (
        <VStack align="stretch" gap="1">
          <Text px="3" pb="1" {...RAIL_MENU_LABEL}>
            Suggested
          </Text>
          {suggestions.map(item => (
            <Button
              key={item.id}
              variant="ghost"
              h="auto"
              display="block"
              textAlign="left"
              whiteSpace="normal"
              px="3"
              py="2.5"
              borderRadius="md"
              onClick={item.onSelect}
              _hover={{ bg: 'ui.surface.rowHover' }}
              _focusVisible={{ outline: '2px solid', outlineColor: 'ui.border.accent' }}
            >
              <Text fontSize="14px" lineHeight="20px" fontWeight="500" color="ui.text.primary">
                {item.name}
              </Text>
              <Text mt="1" fontSize="xs" fontWeight="400" color="ui.text.secondary" lineClamp={2}>
                {item.description}
              </Text>
            </Button>
          ))}
        </VStack>
      ) : null}
    </VStack>,
    target
  );
}
